import _ from 'lodash';

import { fileLocal, issueLocal } from '~/data/local';
import { issueRemote } from '~/data/remote';
import { syncTypes } from '~/sqlite/syncDataTypes';
import { IssuePayload } from '~/types';

import { SyncQueueRow } from '~/data/sync/syncQueueTypes';

import { IssueQueuePayload, SyncHandlerOutcome, SyncTypeHandler } from './types';

export class IssueSyncHandler implements SyncTypeHandler {
  readonly syncType = syncTypes.ADD_ISSUE;

  async process(
    row: SyncQueueRow,
    requestData: Record<string, unknown>
  ): Promise<SyncHandlerOutcome> {
    const localFilePathsToDelete: string[] = [];
    let syncImages: (string | File)[] | string | undefined = undefined;
    let localImageURLs: string[] = [];
    let imagesURLs: string[] = [];

    const isOfflineCreated = Boolean(requestData?.isCreatedFromOfflineMode);
    const localIssueId = requestData?.id !== undefined && requestData?.id !== null ? String(requestData.id) : undefined;
    const localIssueGuid = requestData?.guid ? String(requestData.guid) : undefined;

    if (row.images) {
      try {
        imagesURLs = JSON.parse(row.images);
      } catch {
        imagesURLs = [];
      }
      if (Array.isArray(imagesURLs) && imagesURLs.length) {
        localImageURLs = imagesURLs.filter((path) => _.isString(path) && !path.startsWith('http'));
        const remoteImages = imagesURLs.filter((path) => _.isString(path) && path.startsWith('http'));
        const imageBlobs = await fileLocal.getBlobsFromURLs(localImageURLs);
        syncImages = [...remoteImages, ...imageBlobs.filter(Boolean)];
      } else if (
        row.images === '[]' ||
        row.images === '' ||
        (Array.isArray(imagesURLs) && imagesURLs.length === 0)
      ) {
        syncImages = (!isOfflineCreated && requestData?.id) ? '' : [];
      }
    }

    let syncFiles: (string | File)[] | string | undefined = requestData.files as
      | (string | File)[]
      | string
      | undefined;
    let filesURLs: string[] = [];

    if (Array.isArray(requestData.files) && requestData.files.length) {
      const paths = requestData.files as string[];
      filesURLs = paths.filter((path) => _.isString(path) && !path.startsWith('http'));
      const remoteFiles = paths.filter((path) => _.isString(path) && path.startsWith('http'));
      const files = await fileLocal.getBlobsFromURLs(filesURLs);
      syncFiles = [...remoteFiles, ...files.filter(Boolean)];
    } else if (
      requestData.files === '' ||
      (Array.isArray(requestData.files) && requestData.files.length === 0)
    ) {
      syncFiles = '';
    }

    const payloadToSave: Record<string, unknown> = {
      ...(requestData as IssueQueuePayload),
      ...(syncImages !== undefined ? { images: syncImages } : {}),
      ...(!isOfflineCreated && requestData?.id && syncImages !== undefined ? { updateImages: 1 } : {}),
      ...(syncFiles !== undefined ? { files: syncFiles } : {}),
      ...(!isOfflineCreated && requestData?.id && syncFiles !== undefined ? { updateFiles: 1 } : {}),
    };

    if (isOfflineCreated) {
      delete payloadToSave.id;
    }

    // Offline creates already include `guid` (client id). Retries resend the same payload.
    // TODO: confirm the API dedupes on guid so a retried save cannot create a second issue.
    const response = await issueRemote.saveIssue(payloadToSave as IssuePayload);

    localFilePathsToDelete.push(...localImageURLs, ...filesURLs);

    if (isOfflineCreated && localIssueId) {
      try {
        await issueLocal.removeIssueById(localIssueId);
      } catch (err) {
        console.warn(`[IssueSyncHandler] failed to remove local offline issue id=${localIssueId}`, err);
      }
    }
    if (isOfflineCreated && localIssueGuid) {
      try {
        await issueLocal.removeIssueByGuid(localIssueGuid);
      } catch (err) {
        console.warn(`[IssueSyncHandler] failed to remove local offline issue guid=${localIssueGuid}`, err);
      }
    }

    const websocket: SyncHandlerOutcome['websocket'] = {};
    if (!isOfflineCreated && requestData?.id && response?.id !== undefined) {
      websocket.updatedIssuesDetails = [
        { ...(requestData as IssuePayload), id: response.id },
      ];
    } else if (response?.id !== undefined) {
      websocket.issueIds = [response.id];
    }

    return {
      removeQueueRow: true,
      localFilePathsToDelete,
      machineIdToRefresh: requestData.machineId
        ? String(requestData.machineId)
        : undefined,
      websocket,
    };
  }
}

export const issueSyncHandler = new IssueSyncHandler();
